import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { STORAGE_KEYS } from '@/constants/storage';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { clearAuth, completeSsoLogin, verifyMfaLogin } from '@/store/authSlice';
import { getOrCreateDeviceId } from '@/core/auth/deviceId';
import { showApiError } from '@/utils/apiError';
import {
  clearPlatformOnboardingRequired,
  isFattahOneProductSelectionRequired,
  isPlatformOnboardingRequired,
  requireFattahOneOnboarding,
  writeOnboardingState,
  type OnboardingModule,
} from '@/modules/onboarding/onboardingState';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { getPostLoginPath } from '@/utils/moduleScope';
import { isOrgOwner, resolveOwnerHomePath } from '@/utils/moduleAccess';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { notify } from '@/core/toast/notify';
import type { LoginResponse } from '@/types/auth';
import type { AppDispatch } from '@/store';

const ONBOARDING_MODULES = new Set<OnboardingModule>([
  'POS',
  'CRM',
  'HR',
  'FINANCE',
  'PROJECTS',
  'DESK',
  'SOCIAL',
]);

function resolveOnboardingModule(code?: string): OnboardingModule | undefined {
  if (!code) return undefined;
  const normalized = code.trim().toUpperCase() as OnboardingModule;
  return ONBOARDING_MODULES.has(normalized) ? normalized : undefined;
}

type SsoContext = {
  redirect?: string | null;
  newAccount: boolean;
  pendingEntryModule?: string;
};

function ssoExchangeGuardKey(exchangeToken: string) {
  return `oauth:sso-exchange:${exchangeToken}`;
}

function isMasterConsoleRedirect(redirect?: string | null) {
  return redirect === '/master' || (redirect?.startsWith('/master/') ?? false);
}

function navigateAfterLogin(
  navigate: ReturnType<typeof useNavigate>,
  dispatch: AppDispatch,
  result: LoginResponse,
  context: SsoContext,
) {
  const { redirect, newAccount, pendingEntryModule } = context;
  if (isMasterConsoleRedirect(redirect)) {
    if (!result.masterAdmin) {
      dispatch(clearAuth());
      notify.error('This account is not a platform master administrator.');
      navigate('/master/login', { replace: true });
      return;
    }
    navigate('/master', { replace: true });
    return;
  }
  if (result.masterAdmin) {
    dispatch(clearAuth());
    notify.error('Platform operators must sign in from the master console URL.');
    navigate('/master/login', { replace: true });
    return;
  }
  if (newAccount) {
    const intentModule = resolveOnboardingModule(pendingEntryModule);
    requireFattahOneOnboarding();
    writeOnboardingState({
      step: 0,
      intentModule,
      selectedModules: intentModule ? [intentModule] : ['POS'],
      selectedPlan: 'FREE',
    });
  }
  if (pendingEntryModule) {
    clearPlatformOnboardingRequired();
  }
  if (newAccount || isFattahOneProductSelectionRequired()) {
    navigate(redirect?.startsWith('/') ? redirect : '/onboarding/products', { replace: true });
    return;
  }
  if (isPlatformOnboardingRequired()) {
    navigate('/onboarding', { replace: true });
    return;
  }
  if (result.entryModule || pendingEntryModule) {
    const moduleCode = (result.entryModule ?? pendingEntryModule) as string;
    const home =
      redirect?.startsWith('/')
        ? redirect
        : getModuleEntryByErpModule(moduleCode)?.homePath ?? MODULE_HUB_PATH;
    navigate(getPostLoginPath(moduleCode as never, home), { replace: true });
    return;
  }
  if (isOrgOwner(result.permissions ?? [])) {
    const home = redirect?.startsWith('/') ? redirect : resolveOwnerHomePath(result.enabledModules ?? []);
    navigate(home, { replace: true });
    return;
  }
  navigate(MODULE_HUB_PATH, { replace: true });
}

export function OAuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((s) => s.auth.loading);
  const [error, setError] = useState<string | null>(null);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [ssoContext, setSsoContext] = useState<SsoContext | null>(null);
  const [exchanging, setExchanging] = useState(true);
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    const exchangeToken = params.get('exchangeToken');
    const redirect = params.get('redirect');
    const newAccount = params.get('newAccount') === 'true';
    const entryModuleParam = params.get('entryModule') ?? undefined;
    const pendingEntryModule =
      entryModuleParam ?? sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE) ?? undefined;
    if (!exchangeToken) {
      setError('Missing SSO exchange token');
      setExchanging(false);
      return;
    }
    const guardKey = ssoExchangeGuardKey(exchangeToken);
    if (sessionStorage.getItem(guardKey) === 'done' || exchangeStartedRef.current) {
      return;
    }
    exchangeStartedRef.current = true;
    sessionStorage.setItem(guardKey, 'pending');

    const pictureUrl = params.get('pictureUrl');
    const context: SsoContext = { redirect, newAccount, pendingEntryModule };
    setSsoContext(context);
    (async () => {
      try {
        const result = await dispatch(
          completeSsoLogin({
            exchangeToken,
            deviceId: getOrCreateDeviceId(),
            entryModule: pendingEntryModule,
            oauthPictureUrl: pictureUrl ?? undefined,
          }),
        ).unwrap();
        sessionStorage.setItem(guardKey, 'done');
        if (result.mfaRequired && result.mfaToken) {
          setMfaToken(result.mfaToken);
          setExchanging(false);
          notify.info('Enter the code from your authenticator app', { icon: '🔐' });
          return;
        }
        navigateAfterLogin(navigate, dispatch, result, context);
        notify.success(newAccount ? 'Account created with Google' : 'Signed in with Google');
      } catch (err) {
        sessionStorage.removeItem(guardKey);
        exchangeStartedRef.current = false;
        showApiError(err, 'SSO sign-in failed');
        setError('SSO sign-in failed');
        setExchanging(false);
      }
    })();
  }, [dispatch, navigate, params]);

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || !ssoContext || totpCode.length < 6) return;
    try {
      const result = await dispatch(
        verifyMfaLogin({
          mfaToken,
          totpCode,
          entryModule: ssoContext.pendingEntryModule,
          masterLogin: isMasterConsoleRedirect(ssoContext.redirect),
        }),
      ).unwrap();
      notify.success('Signed in successfully');
      navigateAfterLogin(navigate, dispatch, result, ssoContext);
    } catch (err) {
      showApiError(err, 'Invalid authenticator code');
    }
  };

  if (mfaToken) {
    return (
      <AuthCard title="Two-factor authentication">
        <form onSubmit={onMfaSubmit} className="space-y-4">
          <AuthInput
            label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <AuthButton type="submit" loading={loading} disabled={totpCode.length < 6}>
            Verify and continue
          </AuthButton>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Completing login">
      {error ? (
        <p className="text-center text-sm text-red-600">{error}</p>
      ) : exchanging ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : null}
    </AuthCard>
  );
}
